import { useEffect, useMemo, useState } from 'react';
import { useOtherSettings } from '@namespace/cms';
import { useI18n } from '@namespace/i18n';
import {
  CUSTOM_AMOUNT_OPTION,
  CUSTOM_SESSION_DURATION
} from '../../../../constants';
import {
  Configuration,
  LimitConfig,
  OptionsState,
  UseLimitPresetsReturn
} from '../../types';

const PRESET_TYPE = {
  DEPOSIT: 'depositLimit',
  DURATION: 'durationLimit'
} as const;

const getOptions = (
  config: Configuration,
  type: keyof Configuration,
  t: (value: string) => string
) => {
  const presetsByType = (config[type] as LimitConfig)?.presets || [];
  return presetsByType.map(({ requestValue, displayKeyWeb }) => ({
    title: t(displayKeyWeb),
    value: requestValue
  }));
};

const useLimitPresets = (period: number): UseLimitPresetsReturn => {
  const { t } = useI18n();
  const { uaRegulation = {} } = useOtherSettings();
  const limits: Configuration[] = uaRegulation.limits || [];
  const presets = useMemo(
    () =>
      limits.reduce<Record<number, Configuration>>(
        (acc, limit) => ({
          ...acc,
          [limit.requestValue]: limit
        }),
        {}
      ),
    [limits.length]
  );

  const [options, setOptions] = useState<OptionsState>({
    limit: [],
    duration: []
  });

  const [defaultConfig, setDefaultConfig] = useState({
    depositLimit: {},
    durationLimit: {}
  });

  useEffect(() => {
    if (period) {
      const currentConfigSlice = presets[period];
      const depositLimitConfig = currentConfigSlice?.[PRESET_TYPE.DEPOSIT];
      const durationLimitConfig = currentConfigSlice?.[PRESET_TYPE.DURATION];
      setDefaultConfig({
        depositLimit: depositLimitConfig || {},
        durationLimit: durationLimitConfig || {}
      });
    }
  }, [period, presets]);

  const periodOptions = limits.map(({ titleKeyWeb = '', requestValue }) => ({
    title: t(titleKeyWeb),
    value: requestValue
  }));

  useEffect(() => {
    if (period) {
      const limitOptions = getOptions(presets[period], PRESET_TYPE.DEPOSIT, t);
      const duration = getOptions(presets[period], PRESET_TYPE.DURATION, t);
      const customLimitOption = {
        title: t(
          'web.responsiblegambling.simplifiedinterface.depositlimits.amount.custom'
        ),
        value: CUSTOM_AMOUNT_OPTION
      };
      const customDurationOption = {
        title: t(
          'web.responsiblegambling.simplifiedinterface.depositlimits.sessionduration.custom'
        ),
        value: CUSTOM_SESSION_DURATION
      };

      setOptions({
        limit: [...limitOptions, customLimitOption],
        duration: [...duration, customDurationOption]
      });
    }
  }, [period, presets, t]);

  return {
    defaultConfig: {
      ...defaultConfig
    },
    options: {
      period: periodOptions,
      ...options
    }
  };
};

export default useLimitPresets;
